import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Button,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Checkbox,
    FormControlLabel,
    TextField,
    Typography,
    IconButton,
    Tooltip,
    CircularProgress,
    Grid,
    Paper,
    Divider,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    InputAdornment,
    Alert,
    Snackbar,
    Container
} from '@mui/material';
import {
    Edit as EditIcon,
    Group as GroupIcon,
    Security as SecurityIcon,
    Search as SearchIcon,
    Category as CategoryIcon,
    CheckCircle as CheckCircleIcon,
    Lock as LockIcon,
    LockOpen as LockOpenIcon,
    Refresh as RefreshIcon,
    AdminPanelSettings as AdminPanelSettingsIcon,
    ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { extractList } from '../../utils/response';
import { getRoles, getAllPermissions, getPermissionsByRole, updateRolePermissions as updateRolePermissionsThunk } from '../../store/slices/userSlice';
import PageLayout from '../../components/layout/PageLayout';
import { toast } from 'react-toastify';

const RolesManagement = () => {
    const dispatch = useDispatch();
    const [roles, setRoles] = useState([]);
    const [allPermissions, setAllPermissions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedRole, setSelectedRole] = useState(null);
    const [selectedPermissionIds, setSelectedPermissionIds] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Show snackbar notification
    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    // Fetch roles list
    const fetchRoles = async () => {
        try {
            setLoading(true);
            const response = await dispatch(getRoles()).unwrap();
            const list = extractList(response);
            setRoles(list);
        } catch (error) {
            console.error('Error fetching roles:', error);
            toast.error('Failed to fetch roles', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Fetch all permissions
    const fetchAllPermissions = async () => {
        try {
            const response = await dispatch(getAllPermissions()).unwrap();
            const list = Array.isArray(response) ? response : (response?.data ?? []);
            setAllPermissions(list);
        } catch (error) {
            console.error('Error fetching permissions:', error);
            showSnackbar('Failed to fetch permissions', 'error');
        }
    };

    // Fetch role's current permissions
    const fetchRolePermissions = async (roleId) => {
        try {
            setModalLoading(true);
            const response = await dispatch(getPermissionsByRole(roleId)).unwrap();
            const rolePermissions = response?.data?.permissions ?? response?.permissions ?? [];
            const permissionIds = rolePermissions.map(p => p.id);
            setSelectedPermissionIds(permissionIds);
        } catch (error) {
            console.error('Error fetching role permissions:', error);
            setSelectedPermissionIds([]);
            showSnackbar('Failed to fetch role permissions', 'error');
        } finally {
            setModalLoading(false);
        }
    };

    // Handle edit role permissions
    const handleEditRole = async (role) => {
        setSelectedRole(role);
        setEditModalOpen(true);
        setSelectedPermissionIds([]);
        await fetchRolePermissions(role.id);
    };

    // Handle permission checkbox change
    const handlePermissionChange = (permissionId) => (event) => {
        if (event.target.checked) {
            setSelectedPermissionIds(prev => [...prev, permissionId]);
        } else {
            setSelectedPermissionIds(prev => prev.filter(id => id !== permissionId));
        }
    };

    // Update role permissions
    const handleUpdateRolePermissions = async () => {
        if (!selectedRole) return;

        try {
            setModalLoading(true);

            // Create payload with required fields
            const payload = {
                name: selectedRole.name,
                guard_name: selectedRole.guard_name,
                permissions: selectedPermissionIds
            };

            await dispatch(updateRolePermissionsThunk({ roleId: selectedRole.id, payload })).unwrap();

            showSnackbar('Role permissions updated successfully');
            setEditModalOpen(false);
            fetchRoles();
        } catch (error) {
            console.error('Error updating role permissions:', error);
            showSnackbar(
                error.response?.data?.message || 'Failed to update role permissions',
                'error'
            );
        } finally {
            setModalLoading(false);
        }
    };

    // Filter permissions based on search
    const filteredPermissions = allPermissions.filter(permission =>
        permission.name.toLowerCase().includes(searchText.toLowerCase())
    );

    // Group permissions by category
    const groupPermissionsByCategory = () => {
        const categories = {};

        filteredPermissions.forEach(permission => {
            const category = permission.name.split('-')[0] || 'general';
            if (!categories[category]) {
                categories[category] = [];
            }
            categories[category].push(permission);
        });

        return categories;
    };

    // Get role color
    const getRoleColor = (roleName) => {
        const colors = {
            'Admin': 'error',
            'Operations Manager': 'warning',
            'Support Manager': 'success',
            'Sales Manager': 'primary',
            'Operations Lead': 'info',
            'Support Lead': 'success',
            'Sales Lead': 'secondary',
            'Operations User': 'info',
            'Support User': 'success',
            'Sales User': 'warning',
            'Customer': 'default'
        };
        return colors[roleName] || 'default';
    };

    // Handle page change
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    // Handle rows per page change
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const permissionCategories = groupPermissionsByCategory();
    const paginatedRoles = roles.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    useEffect(() => {
        fetchRoles();
        fetchAllPermissions();
    }, []);

    return (
        <PageLayout>
            <Container maxWidth="xl" sx={{ py: 4 }}>
                    <Box sx={{ p: 3 }}>
                        {/* Header */}
                        <Card sx={{ mb: 3 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <AdminPanelSettingsIcon color="primary" sx={{ fontSize: 32 }} />
                                        <Box>
                                            <Typography variant="h4" component="h1" gutterBottom>
                                                Roles Management
                                            </Typography>
                                            <Typography variant="body1" color="text.secondary">
                                                Manage roles and their permissions
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Chip
                                            icon={<GroupIcon />}
                                            label={`${roles.length} Roles`}
                                            variant="outlined"
                                        />
                                        <Chip
                                            icon={<SecurityIcon />}
                                            label={`${allPermissions.length} Permissions`}
                                            variant="outlined"
                                        />
                                        <Tooltip title="Refresh">
                                            <IconButton onClick={fetchRoles} disabled={loading}>
                                                <RefreshIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>

                        {/* Roles Table */}
                        <Card>
                            <CardContent>
                                <TableContainer>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Role Name</TableCell>
                                                <TableCell>Guard Name</TableCell>
                                                <TableCell>Created At</TableCell>
                                                <TableCell>Status</TableCell>
                                                <TableCell align="center">Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {loading ? (
                                                <TableRow>
                                                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                                        <CircularProgress />
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                paginatedRoles.map((role) => (
                                                    <TableRow key={role.id} hover>
                                                        <TableCell>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <GroupIcon color="primary" fontSize="small" />
                                                                <Typography variant="body1" fontWeight="medium">
                                                                    {role.name}
                                                                </Typography>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip label={role.guard_name} color="primary" variant="outlined" size="small" />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {role.created_at}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                icon={role.deleted_at ? <LockIcon /> : <LockOpenIcon />}
                                                                label={role.deleted_at ? 'Inactive' : 'Active'}
                                                                color={role.deleted_at ? 'error' : 'success'}
                                                                variant="outlined"
                                                                size="small"
                                                            />
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                                                                <Tooltip title="Edit Permissions">
                                                                    <span>
                                                                        <Button
                                                                            variant="contained"
                                                                            size="small"
                                                                            startIcon={<EditIcon />}
                                                                            onClick={() => handleEditRole(role)}
                                                                            disabled={!!role.deleted_at}
                                                                        >
                                                                            Permissions
                                                                        </Button>
                                                                    </span>
                                                                </Tooltip>
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                <TablePagination
                                    rowsPerPageOptions={[5, 10, 25]}
                                    component="div"
                                    count={roles.length}
                                    rowsPerPage={rowsPerPage}
                                    page={page}
                                    onPageChange={handleChangePage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                />
                            </CardContent>
                        </Card>

                        {/* Edit Permissions Dialog */}
                        <Dialog
                            open={editModalOpen}
                            onClose={() => setEditModalOpen(false)}
                            maxWidth="md"
                            fullWidth
                            scroll="paper"
                        >
                            <DialogTitle>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <SecurityIcon color="primary" />
                                    <Typography variant="h6">
                                        Manage Permissions - {selectedRole?.name}
                                    </Typography>
                                </Box>
                            </DialogTitle>

                            <DialogContent dividers>
                                {selectedRole && (
                                    <>
                                        {/* Role Info */}
                                        <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                                            <Grid container spacing={2} alignItems="center">
                                                <Grid item>
                                                    <Chip
                                                        icon={<GroupIcon />}
                                                        label={selectedRole.name}
                                                        color={getRoleColor(selectedRole.name)}
                                                        variant="filled"
                                                    />
                                                </Grid>
                                                <Grid item>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Guard: {selectedRole.guard_name}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Box>

                                        {/* Search */}
                                        <TextField
                                            fullWidth
                                            placeholder="Search permissions..."
                                            value={searchText}
                                            onChange={(e) => setSearchText(e.target.value)}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon color="action" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{ mb: 3 }}
                                        />

                                        <Divider sx={{ mb: 2 }} />

                                        {/* Permissions List */}
                                        {modalLoading ? (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                                <CircularProgress />
                                            </Box>
                                        ) : (
                                            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                                                {Object.keys(permissionCategories).length > 0 ? (
                                                    Object.keys(permissionCategories).map((category) => (
                                                        <Accordion key={category} defaultExpanded>
                                                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    <CategoryIcon color="primary" fontSize="small" />
                                                                    <Typography variant="subtitle1" fontWeight="medium">
                                                                        {category.toUpperCase()}
                                                                    </Typography>
                                                                    <Chip
                                                                        label={permissionCategories[category].length}
                                                                        size="small"
                                                                        variant="outlined"
                                                                    />
                                                                </Box>
                                                            </AccordionSummary>
                                                            <AccordionDetails>
                                                                <Grid container spacing={1}>
                                                                    {permissionCategories[category].map((permission) => (
                                                                        <Grid item xs={12} sm={6} key={permission.id}>
                                                                            <FormControlLabel
                                                                                control={
                                                                                    <Checkbox
                                                                                        checked={selectedPermissionIds.includes(permission.id)}
                                                                                        onChange={handlePermissionChange(permission.id)}
                                                                                        color="primary"
                                                                                    />
                                                                                }
                                                                                label={
                                                                                    <Typography variant="body2" fontFamily="monospace">
                                                                                        {permission.name}
                                                                                    </Typography>
                                                                                }
                                                                            />
                                                                        </Grid>
                                                                    ))}
                                                                </Grid>
                                                            </AccordionDetails>
                                                        </Accordion>
                                                    ))
                                                ) : (
                                                    <Box sx={{ textAlign: 'center', py: 4 }}>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {allPermissions.length === 0
                                                                ? 'No permissions available'
                                                                : 'No permissions found matching your search'}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        )}

                                        {/* Selection Summary */}
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                mt: 2,
                                                p: 2,
                                                bgcolor: 'primary.light',
                                                color: 'primary.contrastText',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}
                                        >
                                            <CheckCircleIcon fontSize="small" />
                                            <Typography variant="body2">
                                                Selected {selectedPermissionIds.length} permission(s)
                                            </Typography>
                                        </Paper>
                                    </>
                                )}
                            </DialogContent>

                            <DialogActions>
                                <Button onClick={() => setEditModalOpen(false)}>
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handleUpdateRolePermissions}
                                    disabled={modalLoading || selectedPermissionIds.length === 0}
                                    startIcon={modalLoading ? <CircularProgress size={16} /> : <CheckCircleIcon />}
                                >
                                    Update Permissions ({selectedPermissionIds.length})
                                </Button>
                            </DialogActions>
                        </Dialog>

                        {/* Snackbar for notifications */}
                        <Snackbar
                            open={snackbar.open}
                            autoHideDuration={6000}
                            onClose={() => setSnackbar({ ...snackbar, open: false })}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        >
                            <Alert
                                onClose={() => setSnackbar({ ...snackbar, open: false })}
                                severity={snackbar.severity}
                                sx={{ width: '100%' }}
                            >
                                {snackbar.message}
                            </Alert>
                        </Snackbar>

                    </Box>
            </Container>
        </PageLayout>
    );
};

export default RolesManagement;